import { Box, Button, Grid, TextField, Theme, Typography, useMediaQuery } from '@mui/material';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { InsertBreakRequest, MobileRecordsVovClient } from '../../../ApiOld/Api/ApiServerVov';
import { useNavigate } from 'react-router';
import LoadingIndicator from '../Shared/LoadingIndicator';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import { Activity, GetCurrentInfo } from '../Shared/ActivityTypeResolver';
import AfterActionScreen from '../Shared/AfterActionScreen';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useSnackbar } from 'notistack';
import MobileCamera from '../Shared/MobileCamera';
import { GeolocationDTO } from './MobileMainActivity';

export interface IMobileActivitiesProps {
}

interface ButtonProps {
    name: string;
    icon: JSX.Element;
    disabled?: boolean;
    onPress: () => void;
}

export default function MobileActivities(props: IMobileActivitiesProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const biggerThanXs = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileRecordsVovClient);
    const { enqueueSnackbar } = useSnackbar();
    const { setPhotoTemp, setMenuHidden, employeeInfo, logout, diagnosticAlerts } = useEmployeeContext();
    const MAIN_ICON_SIZE = biggerThanXs ? 90 : 60;
    const ICON_SIZE = biggerThanXs ? 70 : 50;
    const [loaded, setLoaded] = useState(false);
    const [currentActivity, setCurrentActivity] = useState<Activity | null>();
    const [runningBreak, setRunningBreak] = useState<boolean>(false);
    const [customMessage, setCustomMessage] = useState<JSX.Element | undefined>(undefined);
    const [clickedType, setClickedType] = useState<string | undefined>(undefined);
    const [showCamera, setShowCamera] = useState(false);
    const [location, setLocation] = useState<GeolocationDTO | null>();
    useEffect(() => { setPhotoTemp?.('') }, [setPhotoTemp]);
    const capturePhotoAndNavigate = useCallback((type: string) => {
        navigate('main-activity/' + (clickedType ?? type) + '/' + currentActivity?.buildingId);
    }, [clickedType, currentActivity?.buildingId, navigate]);

    const mainActivityClick = useCallback(() => {
        if (currentActivity?.nextActivityType === '4') {
            setClickedType('4');
            if (!employeeInfo?.takeLoginPhoto) {
                setPhotoTemp('');
                capturePhotoAndNavigate('4');
            }
            else
                setShowCamera(true);
        }
        else {
            navigate('main-activity/' + currentActivity?.nextActivityType);
        }
    }, [capturePhotoAndNavigate, currentActivity?.nextActivityType, employeeInfo?.takeLoginPhoto, navigate, setPhotoTemp]);

    const getLastActivity = useCallback(() => {
        authorizedMobileEmployeeApi.getLastRecordWithBreak().then((record) => {
            if (record) {
                setCurrentActivity(GetCurrentInfo(record));
                setRunningBreak(record.type === "12_1");
                setLoaded(true);
            }
        });
    }, [authorizedMobileEmployeeApi]);

    useEffect(() => {
        //@ts-ignore
        if (window.ReactNativeWebView)
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'get-location' }));
        else {
            window.requestLocationReact();
        }
    }, []);

    window.requestLocationReact = () => {
        if (diagnosticAlerts) {
            enqueueSnackbar('Requesting location', { variant: 'info' });
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        accuracy: position.coords.accuracy,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                },
                (error) => {
                    if (error.code === error.PERMISSION_DENIED) {
                        setLoaded(true);
                        enqueueSnackbar(t('YouNeedToAllowAccessToYourLocationToContinue'), { variant: 'error' });
                    }
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 60000
                }
            );
        }
    };

    window.sendLocation = (latitude: number, longitude: number, accuracy: number) => {
        if (diagnosticAlerts)
            enqueueSnackbar('Accuracy: ' + accuracy + ', Latitude: ' + latitude + ', Longitude: ' + longitude, { variant: 'info' });
        setLocation({
            accuracy: accuracy,
            latitude: latitude,
            longitude: longitude,
        });
    };

    const handleBreak = useCallback((position: GeolocationDTO) => {
        authorizedMobileEmployeeApi.insertBreak(new InsertBreakRequest({
            type: runningBreak ? '12_0' : '12_1',
            accuracy: position.accuracy.toString(),
            gpsX: position.latitude,
            gpsY: position.longitude
        })).then((res) => {
            if (runningBreak) {
                const breakEnd = res.dateTime ? new Date(res.dateTime) : new Date();
                const breakStart = currentActivity?.lastActivityDate
                    ? new Date(currentActivity.lastActivityDate)
                    : null;

                if (breakStart) {
                    const breakDurationMs = breakEnd.getTime() - breakStart.getTime();
                    const breakHours = Math.floor(breakDurationMs / (1000 * 60 * 60));
                    const breakMinutes = Math.floor((breakDurationMs % (1000 * 60 * 60)) / (1000 * 60));

                    setLoaded(true);
                    setCustomMessage(
                        <Typography sx={{ fontSize: 18, color: '#77767B', textAlign: 'center' }} component="div">
                            {t('YourBreakLasted')}
                            <br />
                            <span style={{ fontWeight: 'bold', color: 'black' }}>{breakHours} {t('hours')}</span>{' '}
                            <span style={{ fontWeight: 'normal' }}>{t('and')}</span>{' '}
                            <span style={{ fontWeight: 'bold', color: 'black' }}>{breakMinutes} {t('minutes')}.</span>
                        </Typography>
                    );
                } else {
                    setCustomMessage(
                        <Typography sx={{ fontSize: 18, color: '#FF0000' }} component="div">
                            {t('Unable to calculate the break duration.')}
                        </Typography>
                    );
                    setLoaded(true);
                }
            }
            else if (res.dateTime) {
                setCurrentActivity(currentActivity ? { ...currentActivity, lastActivityDate: new Date(res.dateTime) } : null);
                setLoaded(true);
            }
        });
    }, [authorizedMobileEmployeeApi, currentActivity, runningBreak, t]);

    const insertBreak = useCallback(async () => {
        if (location) {
            handleBreak(location);
        }
        else {
            window.requestLocationReact();
        }
    }, [handleBreak, location]);

    const mainButton: ButtonProps = {
        name: currentActivity?.mainButtonName || '',
        icon: <DirectionsWalkIcon />,
        onPress: () => mainActivityClick(),
    };

    const otherButtons: ButtonProps[] = [
        {
            name: 'Break',
            icon: <TimerOutlinedIcon sx={{ width: ICON_SIZE, height: ICON_SIZE }} />,
            onPress: () => {
                setRunningBreak(true);
                setLoaded(false);
                insertBreak();
            },
            disabled: currentActivity?.breakAndTranstionDisabled,
        },
        {
            name: 'Transition',
            icon: <TransferWithinAStationIcon sx={{ width: ICON_SIZE, height: ICON_SIZE }} />,
            onPress: () => {
                setClickedType('3');
                if (employeeInfo?.takeLoginPhoto) {
                    setShowCamera(true);
                }
                else {
                    setPhotoTemp('');
                    capturePhotoAndNavigate('3');
                }
            },
            disabled: currentActivity?.breakAndTranstionDisabled,
        },
        {
            name: 'Holiday',
            icon: <AirplanemodeActiveIcon sx={{ width: ICON_SIZE, height: ICON_SIZE }} />,
            onPress: () => navigate('range-activity/6'),
        },
        {
            name: 'Doctor',
            icon: <LocalHospitalIcon sx={{ width: ICON_SIZE, height: ICON_SIZE }} />,
            onPress: () => navigate('range-activity/5'),
        },
        {
            name: 'Illness',
            icon: <LocalHotelIcon sx={{ width: ICON_SIZE, height: ICON_SIZE }} />,
            onPress: () => navigate('range-activity/7'),
        }
    ];

    const afterActionScreen = useMemo(() => (
        <AfterActionScreen
            onAction1Click={() => {
                logout();
                navigate('../activities');
            }}
            onAction2Click={() => {
                if (customMessage) {
                    setCustomMessage(undefined);
                    setRunningBreak(false);
                } else {
                    setLoaded(false);
                    insertBreak();
                }
            }}
            action1Text={t('SwitchEmployee')}
            action2Text={customMessage ? 'Continue' : 'EndMyBreak'}
            action1Icon={<PeopleIcon />}
            action2Icon={<ArrowForwardIosIcon />}
            title={customMessage ? 'WelcomeBack!' : 'YouAreRunningABreak!'}
            message={'StartedAt ' + currentActivity?.lastActivityDate?.toTimeString().slice(0, 5)}
            customMessage={customMessage}
            icon={<TimerOutlinedIcon />}
        />
    ), [t, customMessage, currentActivity?.lastActivityDate, logout, navigate, insertBreak]);

    const camera = useMemo(() => {
        return <MobileCamera allowUpload={false} allowMultiple={false} defaultCameraType='user' onBackButtonClicked={() => setShowCamera(false)} onConfirm={(photo) => {
            setPhotoTemp(photo[0].image);
            capturePhotoAndNavigate(clickedType ?? '4');
        }} />;
    }, [capturePhotoAndNavigate, clickedType, setPhotoTemp]);

    useEffect(() => {
        setMenuHidden(false);
        getLastActivity();
    }, [authorizedMobileEmployeeApi, getLastActivity, setMenuHidden]);

    if (!loaded) {
        return <LoadingIndicator />;
    } else if (runningBreak) {
        // setMenuHidden(true);
        return afterActionScreen;
    } else if (showCamera) {
        return camera;
    }

    return (
        <Box>
            <Box sx={{ background: '#FFC600', borderRadius: 2, m: 2, boxShadow: 3 }}>
                <Grid container>
                    <Grid item xs={6} sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2, borderRight: '1px solid #101920', height: '100%' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>
                                {t(currentActivity?.lastActivityText || '') + (currentActivity?.lastActivityText && ":")}
                            </Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 'bold', my: biggerThanXs ? 2 : 0 }}>
                                {t(currentActivity?.lastActivityName ?? '')}
                            </Typography>
                            <Typography sx={{ fontSize: 12 }}>
                                {currentActivity?.lastActivityDate?.toLocaleString(i18n.language)}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            onClick={mainButton.onPress}
                        >
                            <DirectionsWalkIcon sx={{ height: MAIN_ICON_SIZE, width: MAIN_ICON_SIZE }} />
                            <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>{t(mainButton.name)}</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ borderTop: '1px solid #EBEBEB', py: 2, mx: 2 }}>
                <Grid container spacing={1.5}>
                    {otherButtons.map((button, index) => (
                        <Grid item xs={4} sm={3} key={index}>
                            <Box
                                onClick={button.disabled ? undefined : button.onPress}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    boxShadow: 3,
                                    borderRadius: 2,
                                    aspectRatio: 1,
                                    background: button.disabled ? '#EBEBEB' : 'transparent',
                                }}
                            >
                                <Box sx={{ color: button.disabled ? '#D0D0D0' : '#FFC600' }}>
                                    {button.icon}
                                </Box>
                                <Typography sx={{ fontSize: 16 }}>{t(button.name)}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}
